import { ArrowRight, ImageOff } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button, cn, colorTransitionClassName, focusRingClassName } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ProjectCoverTransition } from "@/music-catalog/components/project-cover-transition";
import { ProjectMetaLine } from "@/music-catalog/components/project-detail-page/project-meta-line";
import { formatTrackDuration } from "@/music-catalog/utils/catalog-view";
import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";
import { ROUTES } from "@/navigation/constants/routes";

import type { LibraryProject } from "@/cloud/types/types";
import type { ProjectArtworkPanelId } from "@/music-catalog/utils/project-artwork";

/** A taste of the tracklist, the way the back of a sleeve lists the first side. */
const TRACK_PREVIEW_COUNT = 6;

/**
 * The case with its disc half out of it — the album the way it looks when
 * somebody has just been playing it.
 *
 * The disc is drawn as a disc whether or not it has artwork, because the
 * composition is a CD and not a pair of pictures. The Front is **never**
 * substituted by the disc's own artwork: a differently shaped picture in the
 * case is a different album cover. A picture the browser cannot fetch falls back
 * the same way a missing one does, rather than leaving an empty frame.
 */
const CaseAndDisc = ({ project }: { project: LibraryProject }) => {
  const [failedPanelIds, setFailedPanelIds] = useState<ReadonlySet<ProjectArtworkPanelId>>(
    new Set(),
  );
  const panels = resolveProjectArtworkPanels(project).filter(
    (panel) => !failedPanelIds.has(panel.id),
  );
  const front = panels.find((panel) => panel.id === "front");
  const disc = panels.find((panel) => panel.id === "disc");

  const markFailed = (id: ProjectArtworkPanelId) =>
    setFailedPanelIds((failed) => new Set(failed).add(id));

  return (
    <div className="relative mx-auto aspect-[25/18] w-full max-w-xl lg:mx-0">
      <div
        className={cn(
          "absolute top-1/2 right-0 z-10 aspect-square w-[62%] -translate-y-1/2 overflow-hidden rounded-full shadow-floating ring-1 ring-border",
          "transition-[translate,rotate] duration-700 ease-out group-hover:translate-x-[6%] group-hover:rotate-90",
          "motion-reduce:transition-none motion-reduce:group-hover:translate-x-0 motion-reduce:group-hover:rotate-0",
          // Out from behind the case once a cover travelling into it has landed,
          // rather than already out while it is still on its way.
          "animate-disc-slide-out motion-reduce:animate-none",
        )}
      >
        {disc === undefined ? (
          <div
            data-disc-placeholder
            className="flex size-full items-center justify-center bg-muted"
          >
            <div className="relative size-[34%] rounded-full border border-border bg-card">
              <div className="absolute top-1/2 left-1/2 size-[26%] -translate-1/2 rounded-full bg-background ring-1 ring-border" />
            </div>
          </div>
        ) : (
          // The picture draws its own hole and clear hub, so nothing is laid
          // over it: a second hole drawn here read as a disc with two.
          <img
            src={disc.url}
            alt=""
            className="size-full object-contain"
            onError={() => markFailed(disc.id)}
          />
        )}
      </div>

      <div className="absolute top-0 left-0 z-20 aspect-square w-[72%] overflow-hidden rounded-[3px] bg-card shadow-overlay ring-1 ring-border">
        {front === undefined ? (
          <div
            data-cover-placeholder
            className="flex size-full items-center justify-center bg-muted"
          >
            <ImageOff aria-hidden className="size-14 text-muted-foreground" />
          </div>
        ) : (
          <ProjectCoverTransition desktopProjectId={project.desktopProjectId}>
            <img
              src={front.url}
              alt=""
              className="size-full object-contain"
              onError={() => markFailed(front.id)}
            />
          </ProjectCoverTransition>
        )}
      </div>
    </div>
  );
};

/**
 * The most recently synced album, given the front of the page.
 *
 * Not a card and not a carousel: one record, set large, with the facts under its
 * name and the start of its tracklist, the way a record shop puts the new
 * arrival in the window rather than in a grid with everything else.
 */
export const LatestProject = ({
  duplicateCount,
  project,
}: {
  duplicateCount: number;
  project: LibraryProject;
}) => {
  const { t } = useTranslation();
  const href = `${ROUTES.catalog}/${encodeURIComponent(project.desktopProjectId)}`;
  const shownTracks = project.tracks.slice(0, TRACK_PREVIEW_COUNT);
  const hiddenTrackCount = project.tracks.length - shownTracks.length;

  return (
    <article className="grid items-center gap-8 sm:gap-10 lg:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)] lg:gap-16">
      {/*
        The picture is a second way in for a pointer, and hidden from the keyboard
        and screen readers, which reach the same page through the title.
      */}
      <Link to={href} tabIndex={-1} aria-hidden className="group block">
        <CaseAndDisc project={project} />
      </Link>

      <div className="flex min-w-0 flex-col items-center gap-4 text-center sm:gap-5 lg:items-start lg:text-left">
        <p className="text-xs font-semibold tracking-[0.22em] text-primary uppercase">
          {t("home.latest")}
        </p>

        <h2 className="font-display text-4xl leading-[0.95] font-semibold tracking-tight text-balance sm:text-6xl xl:text-7xl">
          <Link
            to={href}
            className={cn(
              "rounded-sm hover:text-primary",
              colorTransitionClassName,
              focusRingClassName,
            )}
          >
            {project.title}
          </Link>
        </h2>

        <ProjectMetaLine duplicateCount={duplicateCount} project={project} />

        {shownTracks.length > 0 && (
          <ol
            aria-label={t("project.tracklist")}
            className="mt-2 w-full max-w-xl border-t-2 border-foreground text-left"
          >
            {shownTracks.map((track) => {
              const duration = formatTrackDuration(track.durationSeconds);
              return (
                <li
                  key={track.id}
                  className="flex items-baseline gap-3 border-b border-border py-2.5 text-sm sm:gap-4"
                >
                  <span className="w-6 shrink-0 font-display text-muted-foreground tabular-nums">
                    {String(track.position).padStart(2, "0")}
                  </span>
                  {/*
                    One line on a wide screen; on a phone the artist gets its own,
                    or a long title leaves nothing of it but an ellipsis.
                  */}
                  <span className="min-w-0 flex-1 sm:truncate">
                    <span className="block truncate font-medium sm:inline">{track.title}</span>
                    <span className="block truncate text-muted-foreground sm:ml-2 sm:inline">
                      {track.artist}
                    </span>
                  </span>
                  <span className="shrink-0 text-muted-foreground tabular-nums">
                    {duration ?? "—"}
                  </span>
                </li>
              );
            })}
            {hiddenTrackCount > 0 && (
              <li className="py-2.5 text-sm text-muted-foreground">
                {t("home.latest.moreTracks", { count: hiddenTrackCount })}
              </li>
            )}
          </ol>
        )}

        <Button asChild size="lg" className="mt-2 w-full sm:w-auto">
          <Link to={href}>
            {t("catalog.openProject")}
            <ArrowRight />
          </Link>
        </Button>
      </div>
    </article>
  );
};
